import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';

import { chatApi } from '../../api/chat';
import type { PostAttachment, PostType, PostView, ReportReason } from '../../api/community.types';
import { Badge, Button, StreakXpBadge } from '../../ds';
import { WebModal } from '../../shell/WebModal';
import { Avatar } from './Avatar';
import { useSocial } from './SocialContext';
import { SocialIcon, SocialIconButton } from './SocialIcon';
import { SocialHeader } from './SocialHeader';

export const POST_TYPE_LABEL: Record<PostType, string> = {
  QA: 'Hỏi đáp',
  SHARE: 'Chia sẻ',
  FIND_BUDDY: 'Tìm bạn học',
  ACHIEVEMENT: 'Thành tích',
  APP_SUPPORT: 'Hỗ trợ app Torii',
  OTHER: 'Khác',
};

export function PostBadges({ post }: { post: PostView }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {/* Ember chỉ dành cho gamification — badge Thành tích là chỗ duy nhất ở đây được dùng. */}
      <Badge tone={post.type === 'ACHIEVEMENT' ? 'ember' : 'neutral'}>{post.customTopic ?? POST_TYPE_LABEL[post.type]}</Badge>
      {post.solved && <Badge tone="success">Đã giải đáp</Badge>}
    </div>
  );
}

const actionStyle: CSSProperties = {
  flex: 1,
  minHeight: 44,
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  fontFamily: 'var(--font-vi)',
  fontSize: 13,
  fontWeight: 600,
  borderRadius: 'var(--radius-control)',
};

/** Hàng action: chỉ Thích và Bình luận. Đã thích đổi sang navy-500 — không dùng đỏ. */
export function PostActions({ post, onLike, onComment }: { post: PostView; onLike: () => void; onComment?: () => void }) {
  const likeColor = post.liked ? 'var(--navy-500)' : 'var(--text-secondary)';
  return (
    <div style={{ display: 'flex', borderTop: '1px solid var(--border-default)', margin: '0 -4px' }}>
      <button
        type="button"
        className="sk-press"
        aria-label={post.liked ? 'Bỏ thích' : 'Thích'}
        aria-pressed={post.liked}
        style={{ ...actionStyle, color: likeColor }}
        onClick={(e) => {
          e.stopPropagation();
          onLike();
        }}
      >
        <SocialIcon name="heart" size={19} fill={post.liked ? 'var(--navy-500)' : 'none'} />
        {post.likeCount}
      </button>
      <button
        type="button"
        className="sk-press"
        aria-label="Bình luận"
        disabled={!onComment}
        style={{ ...actionStyle, color: 'var(--text-secondary)', cursor: onComment ? 'pointer' : 'default' }}
        onClick={(e) => {
          e.stopPropagation();
          onComment?.();
        }}
      >
        <SocialIcon name="comment" size={19} />
        {post.commentCount}
      </button>
    </div>
  );
}

const REPORT_REASONS: { value: ReportReason; label: string }[] = [
  { value: 'SPAM', label: 'Spam, quảng cáo' },
  { value: 'OFFENSIVE', label: 'Thiếu tôn trọng, xúc phạm' },
  { value: 'WRONG_INFO', label: 'Thông tin sai' },
  { value: 'OTHER', label: 'Lý do khác' },
];

export function ReportDialog({ onClose, onReport }: { onClose: () => void; onReport: (reason: ReportReason) => void }) {
  return (
    <WebModal width={420} onClose={onClose}>
      <SocialHeader title="Báo cáo bài viết" sub="Torii sẽ xem lại bài này" right={<SocialIconButton name="close" label="Đóng" onClick={onClose} />} />
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {REPORT_REASONS.map((r) => (
          <Button key={r.value} variant="secondary" style={{ justifyContent: 'center' }} onClick={() => onReport(r.value)}>
            {r.label}
          </Button>
        ))}
        <Button variant="ghost" style={{ justifyContent: 'center' }} onClick={onClose}>
          Huỷ
        </Button>
      </div>
    </WebModal>
  );
}

/** Menu "…" của bài: bài của mình thì Xoá, bài người khác thì Báo cáo. */
export function PostOptionsMenu({ mine, onDelete, onReport, onClose }: { mine: boolean; onDelete: () => void; onReport: () => void; onClose: () => void }) {
  useEffect(() => {
    document.addEventListener('click', onClose);
    return () => document.removeEventListener('click', onClose);
  }, [onClose]);
  const item: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    minHeight: 44,
    padding: '0 14px',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'var(--font-vi)',
    fontSize: 14,
    fontWeight: 600,
    textAlign: 'left',
    borderRadius: 'var(--radius-control)',
  };
  return (
    <div
      role="menu"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        top: 48,
        right: 0,
        zIndex: 20,
        minWidth: 200,
        padding: 6,
        background: 'var(--surface-page)',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-card)',
        boxShadow: 'var(--shadow-raised)',
      }}
    >
      {mine ? (
        <button type="button" role="menuitem" className="sk-press" style={{ ...item, color: 'var(--error)' }} onClick={onDelete}>
          Xoá bài
        </button>
      ) : (
        <button type="button" role="menuitem" className="sk-press" style={{ ...item, color: 'var(--ink)' }} onClick={onReport}>
          <SocialIcon name="flag" size={17} color="var(--text-secondary)" />
          Báo cáo bài viết
        </button>
      )}
    </div>
  );
}

const box: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 12,
  padding: 14,
  background: 'var(--surface-tint)',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};
const rowBox: CSSProperties = { ...box, flexDirection: 'row', alignItems: 'center', gap: 12 };

function Jp({ children, size }: { children: ReactNode; size: number }) {
  return <span style={{ fontFamily: 'var(--font-jp)', fontSize: size, fontWeight: 500, color: 'var(--ink)' }}>{children}</span>;
}

function JoinGroupButton({ attachment }: { attachment: Extract<PostAttachment, { kind: 'group' }> }) {
  const { toast } = useSocial();
  const [joined, setJoined] = useState(attachment.joined);
  const [joining, setJoining] = useState(false);
  return (
    <Button
      size="sm"
      variant={joined ? 'secondary' : 'primary'}
      disabled={joined || joining}
      onClick={async (e) => {
        e.stopPropagation();
        setJoining(true);
        try {
          await chatApi.joinGroup(attachment.conversationId);
          setJoined(true);
          toast('Đã gửi yêu cầu vào nhóm');
        } catch {
          toast('Không vào được nhóm, thử lại sau');
        } finally {
          setJoining(false);
        }
      }}
    >
      {joined ? 'Đã xin vào' : 'Xin vào'}
    </Button>
  );
}

/// Chưa có tính năng lưu bộ thẻ của người khác ở backend — nút chỉ ghi nhận tại chỗ.
function SaveDeckButton() {
  const { toast } = useSocial();
  const [saved, setSaved] = useState(false);
  return (
    <Button
      size="sm"
      variant="secondary"
      disabled={saved}
      onClick={(e) => {
        e.stopPropagation();
        setSaved(true);
        toast('Lưu bộ thẻ sắp có');
      }}
    >
      {saved ? 'Đã lưu' : 'Lưu'}
    </Button>
  );
}

export function PostAttachmentView({ attachment }: { attachment: PostAttachment | null }) {
  if (!attachment) return null;
  switch (attachment.kind) {
    case 'jp':
    case 'card': {
      const [text, vi] = attachment.kind === 'jp' ? [attachment.text, attachment.vi] : [attachment.jp, attachment.vi];
      return (
        <div style={box}>
          <Jp size={20}>{text}</Jp>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{vi}</div>
        </div>
      );
    }
    case 'group':
      return (
        <div style={rowBox}>
          <Avatar name={attachment.name} mark={attachment.mark} size={44} tone={1} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>{attachment.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{`${attachment.members}/${attachment.max} thành viên · ${attachment.schedule}`}</div>
          </div>
          <JoinGroupButton attachment={attachment} />
        </div>
      );
    case 'deck':
      return (
        <div style={rowBox}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: 'var(--surface-page)',
              border: '1px solid var(--border-default)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--cta-default)',
              flexShrink: 0,
            }}
          >
            <SocialIcon name="card" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>{`${attachment.name} · ${attachment.count} thẻ`}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'var(--font-jp)' }}>{attachment.sample}</div>
          </div>
          <SaveDeckButton />
        </div>
      );
    case 'streak':
      return (
        <div style={{ ...box, background: 'var(--surface-page)' }}>
          <StreakXpBadge streak={attachment.streak} xp={attachment.xp} />
        </div>
      );
    case 'lesson':
      return (
        <div style={rowBox}>
          <SocialIcon name="book" color="var(--cta-default)" />
          <div style={{ flex: 1, fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>{attachment.title}</div>
        </div>
      );
    default:
      return null;
  }
}
